// frontend/src/contexts/ChatbotContext.tsx
// Migrated to use typedApi for type safety.
import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useApp } from './AppContext';
import { typedApi } from '../services/typedApiClient';
import { getErrorMessage } from '../utils/errorUtils';

// =============================================================================
// TYPES
// =============================================================================

export interface ChatbotContextValue {
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => Promise<void>;
  isLoading: boolean;
  lastError: string | null;
}

// =============================================================================
// CONTEXT
// =============================================================================

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

export const useChatbot = (): ChatbotContextValue => {
  const context = useContext(ChatbotContext);
  if (context === undefined || context === null) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface ChatbotProviderProps {
  children: ReactNode;
}

export const ChatbotProvider = ({ children }: ChatbotProviderProps) => {
  const { user, session, loading: authLoading } = useAuth();
  const { syncingUser } = useApp();
  const [isEnabled, setIsEnabled] = useState(false); // Start disabled until confirmed from backend
  const [isLoading, setIsLoading] = useState(true);  // Start loading until backend confirms
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    const loadPreferences = async () => {
      // First, check localStorage for immediate UI response
      const localPref = localStorage.getItem('chatbot-enabled');
      if (localPref !== null) {
        setIsEnabled(JSON.parse(localPref));
        setIsLoading(false);  // We have a cached value, stop loading
      }

      // Wait for auth to finish loading before attempting API calls
      if (authLoading) {
        return;
      }

      // Wait for user sync to complete - prevents 404 errors for new users
      if (syncingUser) {
        return;
      }

      // If no user, stop loading and keep disabled
      if (!user?.id || !session?.access_token) {
        setIsLoading(false);
        return;
      }

      // Don't set loading here if we already loaded from localStorage
      if (localPref === null) {
        setIsLoading(true);
      }

      try {
        // Fetch from backend using typedApi - use userPreferences (users.preferences table)
        // not studentProfile (student_profile.preferences) which stores AI-learned preferences
        const data = await typedApi.userPreferences.get();

        // Backend returns { preferences: { chatbot_enabled: ... } } after unwrapResponse
        const backendEnabled = data.preferences?.chatbot_enabled ?? true;

        // Update both localStorage and state with backend value
        localStorage.setItem('chatbot-enabled', JSON.stringify(backendEnabled));
        setIsEnabled(backendEnabled);
      } catch (error) {
        console.warn('Failed to load preferences:', getErrorMessage(error));
        setLastError('Failed to load preferences');
        // On error, keep the localStorage value if we had one, otherwise default to true
        if (localPref === null) {
          setIsEnabled(true);
          localStorage.setItem('chatbot-enabled', 'true');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [user, session, authLoading, syncingUser]);

  const setEnabled = useCallback(async (enabled: boolean): Promise<void> => {
    if (isLoading) return;

    if (!user?.id) {
      setLastError('Authentication required');
      throw new Error('Authentication required');
    }

    setIsLoading(true);
    setLastError(null);

    try {
      // Optimistically update UI
      setIsEnabled(enabled);
      localStorage.setItem('chatbot-enabled', JSON.stringify(enabled));

      // Save to backend using typedApi - use userPreferences (app settings)
      await typedApi.userPreferences.update({ chatbot_enabled: enabled });

    } catch (error) {
      console.error('Failed to update chatbot preference:', error);

      // Revert optimistic update
      const revertedState = !enabled;
      setIsEnabled(revertedState);
      localStorage.setItem('chatbot-enabled', JSON.stringify(revertedState));

      setLastError(getErrorMessage(error));
      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [isLoading, user?.id]);

  const value: ChatbotContextValue = useMemo(() => ({
    isEnabled,
    setEnabled,
    isLoading,
    lastError
  }), [isEnabled, setEnabled, isLoading, lastError]);

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  );
};

export default ChatbotContext;
